import React, { Suspense, lazy, useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Search, MapPin, ArrowRight, Briefcase, Building2, FileUp, Phone,
  Award, Sparkles, CheckCircle2, ChevronRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import Seo, { orgSchema } from "@/components/Seo";
import PublicLayout from "@/components/layout/PublicLayout";
import { SectionHeader, Reveal } from "@/components/Section";
import Icon from "@/components/IconMap";
import JobCard from "@/components/JobCard";
import Autocomplete from "@/components/Autocomplete";
import api, { trackEvent } from "@/lib/api";
import { useSite } from "@/context/SiteContext";
import { useCapabilities } from "@/hooks/useCapabilities";

const HeroScene = lazy(() => import("@/components/three/HeroScene"));

const STORY = [
  { n: "01", t: "A candidate appears", d: "A skilled professional begins their career journey." },
  { n: "02", t: "Skills take shape", d: "Experience, qualifications and strengths are mapped." },
  { n: "03", t: "The network connects", d: "Dreamzz HR links talent into our recruitment ecosystem." },
  { n: "04", t: "Employers arrive", d: "Businesses share their exact manpower requirements." },
  { n: "05", t: "Matching happens", d: "We match the right candidate to the right opportunity." },
  { n: "06", t: "Connected", d: "Candidate and employer come together through us." },
  { n: "07", t: "Right Talent. Right Opportunity.", d: "A successful placement — a career and a business, both growing." },
];

function HeroFallback() {
  return (
    <div className="absolute inset-0 overflow-hidden">
      <div className="absolute inset-0 section-radial" />
      <div className="absolute top-1/3 right-1/4 h-64 w-64 rounded-full bg-brand-emerald/20 blur-3xl animate-pulseGlow" />
      <div className="absolute bottom-1/4 left-1/3 h-52 w-52 rounded-full bg-brand-blue/20 blur-3xl animate-pulseGlow" />
    </div>
  );
}

export default function Home() {
  const { settings } = useSite();
  const caps = useCapabilities();
  const navigate = useNavigate();
  const [jobs, setJobs] = useState([]);
  const [services, setServices] = useState([]);
  const [locations, setLocations] = useState([]);
  const [whyUs, setWhyUs] = useState([]);
  const [process, setProcess] = useState([]);
  const [industries, setIndustries] = useState([]);
  const [testimonials, setTestimonials] = useState([]);
  const [q, setQ] = useState("");
  const [loc, setLoc] = useState("");

  useEffect(() => {
    api.get("/jobs?limit=6").then((r) => setJobs(r.data.items)).catch(() => {});
    api.get("/services").then((r) => setServices(r.data)).catch(() => {});
    api.get("/locations").then((r) => setLocations(r.data)).catch(() => {});
    api.get("/why-us").then((r) => setWhyUs(r.data)).catch(() => {});
    api.get("/process").then((r) => setProcess(r.data)).catch(() => {});
    api.get("/industries").then((r) => setIndustries(r.data)).catch(() => {});
    api.get("/testimonials").then((r) => setTestimonials(r.data)).catch(() => {});
  }, []);

  const doSearch = (e) => {
    e?.preventDefault();
    trackEvent("job_search", { q, location: loc });
    const p = new URLSearchParams();
    if (q) p.set("q", q);
    if (loc) p.set("location", loc);
    navigate(`/jobs?${p.toString()}`);
  };

  const show3D = caps.checked && caps.webgl && !caps.mobile && !caps.reducedMotion;

  return (
    <PublicLayout>
      <Seo schema={orgSchema(settings)} />

      {/* HERO */}
      <section className="relative min-h-[92vh] flex items-center pt-24 pb-16 overflow-hidden">
        {show3D ? (
          <div className="absolute inset-y-0 right-0 w-full lg:w-[56%] z-0 pointer-events-none">
            <Suspense fallback={<HeroFallback />}><HeroScene /></Suspense>
          </div>
        ) : (
          <HeroFallback />
        )}
        <div className="absolute inset-0 bg-gradient-to-b from-white/50 via-transparent to-[#F8FAFC] z-[1] pointer-events-none" />

        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 w-full">
          <div className="max-w-2xl">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full glass text-xs mb-6 animate-fadeUp">
              <Sparkles size={14} className="text-brand-emerald" />
              <span className="text-slate-300">Premium Recruitment & Manpower Solutions · Karnataka</span>
            </div>
            <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight leading-[1.05] animate-fadeUp">
              Connecting the <span className="text-gradient">Right Talent</span> With the Right Opportunity
            </h1>
            <p className="mt-6 text-base sm:text-lg text-slate-300 leading-relaxed max-w-xl animate-fadeUp">
              Professional manpower recruitment, staffing and HR solutions for businesses and job seekers across Karnataka.
            </p>
            <div className="mt-8 flex flex-wrap gap-3 animate-fadeUp">
              <Link to="/jobs"><Button data-testid="hero-find-jobs" size="lg" className="btn-press bg-brand-emerald hover:bg-[#C62828] text-white font-semibold glow-primary">
                <Search size={18} className="mr-2" />Find Jobs</Button></Link>
              <Link to="/for-employers"><Button data-testid="hero-hire-talent" size="lg" variant="outline" className="btn-press border-white/20 hover:border-brand-emerald hover:text-brand-emerald bg-white/5">
                <Building2 size={18} className="mr-2" />Hire Talent</Button></Link>
              <Link to="/submit-resume"><Button data-testid="hero-submit-resume" size="lg" variant="ghost" className="btn-press text-slate-200 hover:text-brand-emerald">
                <FileUp size={18} className="mr-2" />Submit Resume</Button></Link>
            </div>
          </div>
        </div>
      </section>

      {/* JOB SEARCH MODULE */}
      <section className="relative -mt-8 z-20 px-4 sm:px-6">
        <div className="max-w-5xl mx-auto glass-strong rounded-2xl p-5 sm:p-6 shadow-2xl">
          <h2 className="font-display font-bold text-lg mb-4">Find Your Next Opportunity</h2>
          <form onSubmit={doSearch} className="grid gap-3 sm:grid-cols-[1fr_1fr_auto]">
            <Autocomplete
              testId="home-search-keyword" field="title" value={q} onChange={setQ}
              placeholder="Job title, skill or keyword"
              icon={<Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 z-10" />}
            />
            <Autocomplete
              testId="home-search-location" field="location" value={loc} onChange={setLoc}
              placeholder="City / location"
              icon={<MapPin size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 z-10" />}
            />
            <Button data-testid="home-search-submit" type="submit" size="lg" className="h-12 btn-press bg-brand-emerald hover:bg-[#C62828] text-white font-semibold px-8">
              Search Jobs
            </Button>
          </form>
        </div>
      </section>

      {/* SERVICES */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 py-20">
        <Reveal><SectionHeader eyebrow="What We Do" title="Recruitment & Staffing Services"
          subtitle="End-to-end manpower and HR solutions tailored for every industry and workforce need." /></Reveal>
        <div className="mt-10 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {services.slice(0, 9).map((s, i) => (
            <Reveal key={s.slug} delay={i * 40}>
              <Link to={`/services/${s.slug}`} data-testid={`service-card-${s.slug}`}
                className="group glass card-hover rounded-2xl p-6 flex flex-col h-full">
                <span className="grid place-items-center h-12 w-12 rounded-xl bg-brand-emerald/15 text-brand-emerald mb-4 group-hover:scale-110 transition-transform">
                  <Icon name={s.icon} size={22} />
                </span>
                <h3 className="font-display font-semibold text-lg">{s.name}</h3>
                <p className="text-sm text-slate-400 mt-2 leading-relaxed flex-1">{s.short}</p>
                <span className="mt-4 inline-flex items-center gap-1 text-sm text-brand-emerald font-medium">
                  View Service <ChevronRight size={16} className="group-hover:translate-x-1 transition-transform" />
                </span>
              </Link>
            </Reveal>
          ))}
        </div>
        <div className="text-center mt-8">
          <Link to="/services"><Button variant="outline" className="border-white/15 hover:border-brand-emerald">All 18 Services <ArrowRight size={16} className="ml-2" /></Button></Link>
        </div>
      </section>

      {/* SCROLL STORY */}
      <section className="section-radial py-20 border-y border-white/5">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <Reveal><SectionHeader center eyebrow="How It Works" title="From Talent to Opportunity"
            subtitle="Follow the journey of how Dreamzz HR connects a candidate to their next career move." /></Reveal>
          <div className="mt-12 grid gap-4 md:grid-cols-2 lg:grid-cols-4">
            {STORY.map((s, i) => (
              <Reveal key={s.n} delay={i * 60}>
                <div className={`glass rounded-2xl p-6 h-full ${i === STORY.length - 1 ? "border-brand-emerald/40 glow-primary" : ""}`}>
                  <span className="font-mono text-brand-emerald text-sm">{s.n}</span>
                  <h3 className="font-display font-semibold mt-2">{s.t}</h3>
                  <p className="text-sm text-slate-400 mt-2">{s.d}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* EMPLOYERS + JOB SEEKERS */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 py-20 grid gap-6 lg:grid-cols-2">
        <Reveal>
          <div className="relative glass card-hover rounded-3xl p-8 overflow-hidden grain h-full">
            <div className="absolute -top-16 -right-16 h-48 w-48 rounded-full bg-brand-blue/20 blur-3xl" />
            <Building2 size={32} className="text-brand-blue" />
            <h3 className="font-display text-2xl font-bold mt-4">Need Staff?</h3>
            <p className="text-slate-400 mt-3">Employer → Requirement → Dreamzz HR → Candidate Database → Shortlisting → Interview → Placement.</p>
            <ul className="mt-5 space-y-2 text-sm text-slate-300">
              {["Access a large verified candidate database", "Structured shortlisting & interviews", "Fast turnaround across Karnataka"].map((t) => (
                <li key={t} className="flex items-center gap-2"><CheckCircle2 size={16} className="text-brand-emerald" />{t}</li>
              ))}
            </ul>
            <div className="mt-6 flex flex-wrap gap-3">
              <Link to="/for-employers"><Button className="btn-press bg-brand-blue hover:bg-[#2563EB] text-white">Submit Hiring Requirement</Button></Link>
              <Link to="/contact"><Button variant="outline" className="border-white/15">Talk to Recruitment Team</Button></Link>
            </div>
          </div>
        </Reveal>
        <Reveal delay={80}>
          <div className="relative glass card-hover rounded-3xl p-8 overflow-hidden grain h-full">
            <div className="absolute -top-16 -right-16 h-48 w-48 rounded-full bg-brand-emerald/20 blur-3xl" />
            <Briefcase size={32} className="text-brand-emerald" />
            <h3 className="font-display text-2xl font-bold mt-4">Looking For A Job?</h3>
            <p className="text-slate-400 mt-3">Industry → Skill → Location → Job → Application → Interview → Career.</p>
            <ul className="mt-5 space-y-2 text-sm text-slate-300">
              {["Genuine local jobs across Karnataka", "Free candidate registration", "Recruiter support at every step"].map((t) => (
                <li key={t} className="flex items-center gap-2"><CheckCircle2 size={16} className="text-brand-emerald" />{t}</li>
              ))}
            </ul>
            <div className="mt-6 flex flex-wrap gap-3">
              <Link to="/jobs"><Button className="btn-press bg-brand-emerald hover:bg-[#C62828] text-white">Explore Jobs</Button></Link>
              <Link to="/submit-resume"><Button variant="outline" className="border-white/15">Submit Your Resume</Button></Link>
            </div>
          </div>
        </Reveal>
      </section>

      {/* FEATURED JOBS */}
      {jobs.length > 0 && (
        <section className="max-w-7xl mx-auto px-4 sm:px-6 py-10">
          <Reveal><div className="flex items-end justify-between flex-wrap gap-4">
            <SectionHeader eyebrow="Open Positions" title="Latest Job Openings" />
            <Link to="/jobs" className="text-brand-emerald text-sm font-medium inline-flex items-center gap-1 hover:gap-2 transition-all">View all jobs <ArrowRight size={16} /></Link>
          </div></Reveal>
          <div className="mt-8 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {jobs.map((j, i) => <Reveal key={j.id} delay={i * 40}><JobCard job={j} /></Reveal>)}
          </div>
        </section>
      )}

      {/* WHY US */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 py-20">
        <Reveal><SectionHeader center eyebrow="Why Dreamzz HR" title="Built on Trust, Technology & People" /></Reveal>
        <div className="mt-10 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {whyUs.map((w, i) => (
            <Reveal key={w.slug} delay={i * 50}>
              <div className="glass card-hover rounded-2xl p-6 flex items-start gap-4">
                <span className="grid place-items-center h-12 w-12 shrink-0 rounded-xl bg-gradient-to-br from-brand-emerald/20 to-brand-blue/20 text-brand-emerald animate-floaty">
                  <Icon name={w.icon} size={22} />
                </span>
                <div>
                  <h3 className="font-display font-semibold">{w.title}</h3>
                  <p className="text-sm text-slate-400 mt-1">{w.description}</p>
                </div>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* PROCESS TIMELINE */}
      <section className="section-radial py-20 border-y border-white/5">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <Reveal><SectionHeader center eyebrow="Our Process" title="A Structured Recruitment Pipeline" /></Reveal>
          <div className="mt-12 relative">
            <div className="hidden lg:block absolute top-8 left-0 right-0 h-px bg-gradient-to-r from-transparent via-brand-emerald/40 to-transparent" />
            <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-7">
              {process.map((p, i) => (
                <Reveal key={p.number} delay={i * 50}>
                  <div className="relative text-center">
                    <span className="relative z-10 grid place-items-center h-16 w-16 mx-auto rounded-2xl glass-strong font-mono text-brand-emerald font-bold text-lg glow-ring">{p.number}</span>
                    <h3 className="font-display font-semibold text-sm mt-4">{p.title}</h3>
                    <p className="text-xs text-slate-400 mt-1.5">{p.description}</p>
                  </div>
                </Reveal>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* INDUSTRIES */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 py-20">
        <Reveal><SectionHeader eyebrow="Industries" title="Recruitment Across Every Sector"
          subtitle="We serve a wide range of industries with specialised staffing expertise." /></Reveal>
        <div className="mt-10 flex flex-wrap gap-3">
          {industries.map((ind, i) => (
            <Link key={ind.slug} to={`/industries/${ind.slug}`} data-testid={`industry-chip-${ind.slug}`}
              className="glass card-hover rounded-full px-5 py-2.5 text-sm font-medium hover:text-brand-emerald">
              {ind.name}
            </Link>
          ))}
        </div>
      </section>

      {/* LOCATIONS */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 py-10">
        <Reveal><SectionHeader eyebrow="Where We Operate" title="Serving Key Locations Across Karnataka" /></Reveal>
        <div className="mt-8 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
          {locations.map((l, i) => (
            <Reveal key={l.slug} delay={i * 30}>
              <Link to={`/locations/${l.slug}`} data-testid={`location-card-${l.slug}`}
                className="group glass card-hover rounded-xl p-4 flex items-center gap-3">
                <MapPin size={18} className="text-brand-emerald" />
                <div>
                  <div className="font-display font-semibold group-hover:text-brand-emerald transition-colors">{l.name}</div>
                  <div className="text-xs text-slate-500">{l.district}</div>
                </div>
              </Link>
            </Reveal>
          ))}
        </div>
      </section>

      {/* TESTIMONIALS */}
      {testimonials.length > 0 && (
        <section className="max-w-7xl mx-auto px-4 sm:px-6 py-20">
          <Reveal><SectionHeader center eyebrow="Testimonials" title="Trusted by Employers & Job Seekers" /></Reveal>
          <div className="mt-10 grid gap-5 lg:grid-cols-3">
            {testimonials.map((t, i) => (
              <Reveal key={i} delay={i * 60}>
                <div className="glass rounded-2xl p-6 h-full">
                  <div className="flex gap-1 text-brand-gold mb-3">{Array.from({ length: t.rating || 5 }).map((_, k) => <span key={k}>★</span>)}</div>
                  <p className="text-slate-300 leading-relaxed">"{t.text}"</p>
                  <div className="mt-4 pt-4 border-t border-white/5">
                    <div className="font-display font-semibold">{t.name}</div>
                    <div className="text-xs text-slate-500">{t.role}</div>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </section>
      )}

      {/* FINAL CTA */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 py-16">
        <div className="relative overflow-hidden rounded-3xl glass-strong p-8 sm:p-12 text-center grain">
          <div className="absolute -top-24 left-1/2 -translate-x-1/2 h-64 w-64 rounded-full bg-brand-emerald/20 blur-3xl" />
          <div className="relative">
            <Award size={40} className="text-brand-emerald mx-auto" />
            <h2 className="font-display text-3xl sm:text-4xl font-bold mt-4">Need Staff? Let's Build Your Team.</h2>
            <p className="text-slate-400 mt-3 max-w-xl mx-auto">Share your manpower requirement and our recruitment team will get back to you with the right candidates.</p>
            <div className="mt-7 flex flex-wrap gap-3 justify-center">
              <Link to="/for-employers"><Button size="lg" className="btn-press bg-brand-emerald hover:bg-[#C62828] text-white font-semibold glow-primary">Submit Hiring Requirement</Button></Link>
              <a href={`tel:${settings.phone}`} onClick={() => trackEvent("phone_click")}><Button size="lg" variant="outline" className="border-white/20"><Phone size={18} className="mr-2" />Call Us</Button></a>
            </div>
          </div>
        </div>
      </section>
    </PublicLayout>
  );
}
